import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const EmployeePortal = ({ employees, attendanceRecords }) => {
  const [dni, setDni] = useState('');
  const [employee, setEmployee] = useState(null);
  const [myRecords, setMyRecords] = useState([]);
  const navigate = useNavigate();

  const handleLogin = (e) => {
    e.preventDefault();
    const found = employees.find(emp => emp.dni === dni.trim());
    if (found) {
      setEmployee(found);
      const records = attendanceRecords.filter(r => r.dni === found.dni);
      setMyRecords(records);
    } else {
      alert("DNI no encontrado. Consulte con recursos humanos.");
    }
  };

  const handleLogout = () => {
    setEmployee(null);
    setDni('');
    setMyRecords([]);
  };

  if (employee) {
    return (
      <div className="max-w-4xl mx-auto animate-fade-in-up">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-100">Hola, {employee.nombre}</h2>
            <p className="text-slate-400">Portal de Auto-consulta de Asistencia</p>
          </div>
          <button 
            onClick={handleLogout}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm transition-colors"
          >
            Cerrar Sesión
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-card p-6 rounded-2xl border-slate-800/80">
            <h3 className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">DNI</h3>
            <p className="text-2xl font-mono text-slate-200">{employee.dni}</p>
          </div>
          <div className="glass-card p-6 rounded-2xl border-slate-800/80">
            <h3 className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Horario</h3>
            <p className="text-2xl font-mono text-slate-200">
              {employee.scheduleStart || '08:00'} - {employee.scheduleEnd || '17:00'}
            </p>
          </div>
          <div className="glass-card p-6 rounded-2xl border-slate-800/80">
            <h3 className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Total Marcas</h3>
            <p className="text-2xl font-mono text-slate-200">{myRecords.length}</p>
          </div>
        </div>

        <div className="glass-card rounded-2xl border-slate-800/80 overflow-hidden">
          <div className="p-6 border-b border-slate-800">
            <h3 className="text-lg font-bold text-slate-100">Mi Historial de Asistencia</h3>
          </div>
          {myRecords.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No tienes registros de asistencia aún.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-900/50">
                  <tr className="text-slate-400 text-xs uppercase tracking-wider">
                    <th className="p-4 font-medium">Fecha</th>
                    <th className="p-4 font-medium">Hora</th>
                    <th className="p-4 font-medium">Tipo</th>
                    <th className="p-4 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {myRecords.map(record => {
                    let isLate = false;
                    if (record.type === 'entrada') {
                      isLate = record.time > (employee.scheduleStart || '08:00') + ':59';
                    }
                    
                    return (
                      <tr key={record.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="p-4 text-sm text-slate-300">{record.date}</td>
                        <td className="p-4 text-sm font-mono text-slate-300">{record.time}</td>
                        <td className="p-4 text-sm">
                          <span className={`px-2 py-1 rounded-md text-xs font-medium capitalize ${
                            record.type === 'entrada' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                          }`}>
                            {record.type}
                          </span>
                        </td>
                        <td className="p-4 text-sm">
                          {isLate ? (
                            <span className="px-2 py-1 bg-amber-500/10 text-amber-400 rounded-md text-xs font-medium">
                              Tardanza
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-800 text-slate-400 rounded-md text-xs font-medium">
                              Normal
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-12 animate-fade-in-up">
      <div className="text-center mb-8">
        <button onClick={() => navigate('/')} className="text-cyan-400 hover:text-cyan-300 text-sm font-medium mb-8 flex items-center justify-center gap-2 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Volver al Inicio
        </button>
        <div className="w-16 h-16 bg-indigo-500/10 text-indigo-400 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
          </svg>
        </div>
        <h2 className="text-3xl font-extrabold text-slate-100">Portal del Empleado</h2>
        <p className="text-slate-400 mt-2">Consulta tus registros de asistencia</p>
      </div>

      <div className="glass-card p-8 rounded-2xl border-slate-800/80">
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Número de DNI
            </label>
            <input
              type="text"
              required
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-600 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/25 transition-all"
              placeholder="Ingrese su DNI..."
            />
          </div>
          <button
            type="submit"
            className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/20 transition-all duration-200"
          >
            Ingresar
          </button>
        </form>
      </div>
    </div>
  );
};

export default EmployeePortal;
